/**
 * PlatformAgencyDetailPage — the Platform Operator's agency review and
 * decision screen.
 *
 * Route: `/app/platform/commercial/:organizationId`.
 * Task 307A §1 items 1-4. Task file:
 * `tasks/307A-platform-operator-console.md`.
 *
 * ---------------------------------------------------------------------
 * A ROUTE, NOT A DISCLOSURE, AND THE DIFFERENCE IS THE PRODUCT.
 *
 * 306A §1.4 expanded the detail inside the list row. That surface could
 * not be linked to, did not survive a refresh, and had no Back — the
 * operator's own history did not record which agency they had opened. It
 * is a route now: the URL names the tenant, refresh restores it,
 * Back/Forward move between agencies, and the list query the operator
 * came from travels in the search string so «كل الوكالات» returns to the
 * filtered page they left rather than to an unfiltered first page.
 *
 * THIS IS WHERE THE DECISION IS COMMITTED, AND IT IS THE ONLY PLACE.
 * The list navigates; it does not transact. Two ways to commit one
 * transition is a defect class, not a redundancy.
 *
 * WHAT THE OPERATOR IS SHOWN, AND WHY EACH PIECE IS HERE:
 *   - the review checklist — the Task 299 legal profile, eight fields
 *     displayed and SIX scored, because six is what
 *     `app.set_organization_profile_completed_at()` requires and telling
 *     an operator that a finished profile is "6 of 8" invents a
 *     requirement the product does not have;
 *   - the standing, its dates and the platform's own notes;
 *   - the SaaS subscription AND the manual plan control, which exists
 *     because 295A D1 keeps payments dormant: there is no checkout, so
 *     the plan is an operator decision;
 *   - both append-only ledgers, decisions and plan assignments, because
 *     an audit record nobody can read is not auditable.
 *
 * WHAT IT DELIBERATELY DOES NOT SHOW: any client row, any tenant user,
 * any auth uuid, any storage key, and any usage or limit figure. The
 * endpoint returns none of them and this page asks for nothing else.
 * ---------------------------------------------------------------------
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  getOrganizationCommercialDetail,
  setOrganizationCommercialState,
  setOrganizationSubscriptionPlan,
} from '../../api/endpoints';
import { AGENCY_PROFILE_FIELD_KEYS } from '../../api/types';
import type {
  CommercialAction,
  CommercialState,
  OrganizationCommercialDetailData,
} from '../../api/types';
import { useMeContext } from '../../context/MeContextProvider';
import {
  ErrorState,
  LoadingState,
  NotFoundState,
  PermissionDeniedState,
} from '../../components/states';
import { isApiError } from '../../api/client';
import { useLatestRef } from './useLatestRef';
import {
  ACTION_LABELS,
  COMMERCIAL_STATE_LABELS_AR,
  DESTRUCTIVE_ACTION_WARNING,
  LEDGER_ACTION_LABELS_AR,
  PAYMENT_STATUS_LABELS_AR,
  PLAN_ASSIGNMENT_BLOCKER_MESSAGES_AR,
  actionsForState,
  agencyListPath,
  attachedIds,
  completionPercent,
  createLatestPerKey,
  createOrganizationActionLock,
  displayValue,
  formatOperatorTimestamp,
  isDestructiveAction,
  planAssignmentBlocker,
  planLabel,
  profileFieldLabel,
  runCommercialAction,
  runCommercialDetailLoad,
  standingBanner,
  subscriptionStateLabel,
  unattachedActionErrors,
  withActionError,
  withBusy,
} from './operatorConsole';
import type {
  CommercialActionError,
  DetailState,
  LatestPerKey,
  OrganizationActionLock,
} from './operatorConsole';
import { CompletionMeter, StandingPill } from './PlatformCommercialPage';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** What the operator is being asked to confirm, or `null` for no dialog. */
export type PendingDecision = {
  readonly action: CommercialAction;
  readonly organizationName: string;
};

export function PlatformAgencyDetailPage() {
  const { me } = useMeContext();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const organizationId = (params.organizationId ?? '').trim();

  const [details, setDetails] = useState<Readonly<Record<string, DetailState>>>({});
  const [busyOrgs, setBusyOrgs] = useState<ReadonlySet<string>>(() => new Set());
  const [actionErrors, setActionErrors] = useState<Readonly<Record<string, CommercialActionError>>>(
    {},
  );
  const [pending, setPending] = useState<PendingDecision | null>(null);
  const [reason, setReason] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const [planKey, setPlanKey] = useState('');
  const [planReason, setPlanReason] = useState('');
  const [planBlocker, setPlanBlocker] = useState<string | null>(null);

  /**
   * A PLAN failure is not a STANDING failure, and they may not share a slot.
   *
   * They did, and the slot is the one `actionFailurePlacement` moves INTO
   * the decision dialog: a refused plan assignment could therefore be
   * announced inside an open «إيقاف مؤقت» confirmation, over a transition
   * that had not been attempted, while the plan controls that produced it
   * showed nothing. One slot per operation, each rendered where its own
   * controls are, and neither can overwrite the other's unread report.
   */
  const [planErrors, setPlanErrors] = useState<Readonly<Record<string, CommercialActionError>>>({});

  /**
   * The organization whose plan assignment COMMITTED in this session — the
   * outcome the refresh must not be able to erase.
   *
   * `set-organization-subscription-plan` returning 200 is the write; the
   * detail read that follows it is only the picture of it. Before this the
   * page kept nothing at all: on a successful assignment the operator saw
   * the panel blink and had to compare plan names to learn whether
   * anything had happened, and when the refresh FAILED they were shown
   * «لم يُفقد أي شيء ولم يتغيّر أي سجل» — over a plan change that was
   * already in the ledger — with a retry button beside it. Retrying is
   * what turns a committed assignment into a 409 `same_plan`, which reads
   * as a second failure of an operation that in fact succeeded.
   *
   * The id is carried, not a bare flag: a commit that lands after the
   * operator has navigated to another agency belongs to the agency it was
   * made on, and is not reported over a tenant it says nothing about.
   */
  const [planCommittedFor, setPlanCommittedFor] = useState<string | null>(null);
  const [standingCommittedFor, setStandingCommittedFor] = useState<string | null>(null);

  /**
   * One generation token PER ORGANIZATION, not one for the page.
   *
   * This route can outlive the tenant it is showing: React Router
   * re-renders the same component with a new `:organizationId` rather
   * than unmounting it, so a read for the agency the operator just left
   * can still be open when the next one starts. Keying the token by
   * organization is what stops the older answer being written into the
   * newer agency's panel.
   */
  const latestDetailRef = useRef<LatestPerKey | null>(null);
  if (latestDetailRef.current === null) latestDetailRef.current = createLatestPerKey();
  const latestDetail = latestDetailRef.current;

  // The turnstile the `disabled` attribute only LOOKS like (306A §1.5.2).
  const actionLockRef = useRef<OrganizationActionLock | null>(null);
  if (actionLockRef.current === null) actionLockRef.current = createOrganizationActionLock();
  const actionLock = actionLockRef.current;

  const loadDetail = useCallback(
    (id: string): Promise<void> =>
      runCommercialDetailLoad(id, latestDetail, {
        fetchDetail: (target) => getOrganizationCommercialDetail({ organizationId: target }),
        setDetail: (target, state) => setDetails((prev) => ({ ...prev, [target]: state })),
      }).fresh,
    [latestDetail],
  );

  useEffect(() => {
    if (!me?.system_admin) return;
    if (!UUID_RE.test(organizationId)) return;
    void loadDetail(organizationId);
  }, [loadDetail, me?.system_admin, organizationId]);

  // Navigating to another agency abandons whatever was half-typed for the
  // previous one. Carrying a reason across tenants is how a reason ends
  // up recorded against the wrong ledger.
  useEffect(() => {
    setPending(null);
    setReason('');
    setConfirmName('');
    setPlanKey('');
    setPlanReason('');
    setPlanBlocker(null);
    // The commit notice is one agency's outcome, and it is cleared with
    // the rest of that agency's half-finished work. It is NOT cleared by
    // the failure of the refresh that follows it: that failure is the
    // reason the notice has to survive.
    setPlanCommittedFor(null);
    setStandingCommittedFor(null);
  }, [organizationId]);

  const detail = details[organizationId] ?? null;
  const data = detail?.status === 'ready' ? detail.data : null;
  const busy = busyOrgs.has(organizationId);

  // Read AFTER the request settles, so it goes through a ref: the value
  // this closure captured is the one from the render the operator clicked
  // in (306A §1.5.1).
  const dataRef = useLatestRef(data);

  const setActionErrorFor = useCallback(
    (id: string, message: string | null, organizationName: string) =>
      setActionErrors((prev) =>
        withActionError(prev, id, message === null ? null : { organizationName, message }),
      ),
    [],
  );

  /** The same, for the OTHER slot. Keyed for {@link setActionErrorFor}'s reason. */
  const setPlanErrorFor = useCallback(
    (id: string, message: string | null, organizationName: string) =>
      setPlanErrors((prev) =>
        withActionError(prev, id, message === null ? null : { organizationName, message }),
      ),
    [],
  );

  if (!me) return <LoadingState title="جاري تحميل الجلسة…" />;
  if (!me.system_admin) return <PermissionDeniedState />;

  // The route parameter is validated BEFORE any request. A malformed id
  // is a not-found here rather than a 400 from the wire, and it never
  // reaches the endpoint at all.
  if (!UUID_RE.test(organizationId)) {
    return (
      <div className="page" dir="rtl" data-testid="platform-agency-detail-page">
        <NotFoundState
          title="معرّف وكالة غير صالح"
          description="الرابط لا يشير إلى وكالة. عد إلى القائمة واختر وكالة منها."
          homePath={agencyListPath(searchParams.toString())}
        />
      </div>
    );
  }

  const backPath = agencyListPath(searchParams.toString());

  const runAction = (action: CommercialAction, organizationName: string) => {
    setStandingCommittedFor(null);
    void runCommercialAction(organizationId, action, {
      readReason: () => reason,
      // The dialog IS the confirmation: it carries the mandated
      // client-portal blast-radius warning and, for `terminate`, the
      // typed-name gate — both checked before this is ever called. A
      // second `window.confirm` on top of it would train operators to
      // click through the one warning that matters.
      confirmDestructive: () => true,
      lock: actionLock,
      transition: (validReason) =>
        setOrganizationCommercialState({ organizationId, action, reason: validReason }),
      setBusy: (id, isBusy) => setBusyOrgs((prev) => withBusy(prev, id, isBusy)),
      onApplied: (id) => {
        setPending(null);
        setReason('');
        setConfirmName('');
        setStandingCommittedFor(id);
        // Returns the READ'S FRESHNESS promise, not its settlement, so the
        // claim is held until the panel on screen is at least as new as
        // the transition. On an absorbing move like `terminate` an early
        // release would leave the operator acting on a control the server
        // has already made illegal (306A §1.5.2).
        return loadDetail(id);
      },
      setActionError: (id, message) => setActionErrorFor(id, message, organizationName),
    });
  };

  const runPlanAssignment = (organizationName: string) => {
    const blocker = planAssignmentBlocker({
      planKey,
      reason: planReason,
      assignable: dataRef.current?.assignable_plans ?? [],
    });
    if (blocker !== null) {
      setPlanBlocker(PLAN_ASSIGNMENT_BLOCKER_MESSAGES_AR[blocker]);
      return;
    }
    setPlanBlocker(null);
    // A new attempt supersedes the previous outcome. Leaving the old
    // notice up while this one is in flight would report a commit that
    // this submission has not made.
    setPlanCommittedFor(null);
    setStandingCommittedFor(null);
    void runCommercialAction(organizationId, 'assign_plan', {
      readReason: () => planReason,
      confirmDestructive: () => true,
      // THE SAME LOCK AS THE TRANSITIONS, keyed by organization. A plan
      // assignment and a standing transition on one tenant must not run
      // at once: both write the row the panel is about to re-read, and
      // whichever settled last would decide what the operator sees.
      lock: actionLock,
      transition: (validReason) =>
        setOrganizationSubscriptionPlan({ organizationId, planKey, reason: validReason }),
      setBusy: (id, isBusy) => setBusyOrgs((prev) => withBusy(prev, id, isBusy)),
      onApplied: (id) => {
        // RECORDED BEFORE THE REFRESH IS EVEN ISSUED. By the time this
        // runs the write is committed and the ledger has the row; whether
        // the read that follows succeeds decides how the notice READS
        // (see {@link planCommitNotice}), never whether there is one.
        setPlanCommittedFor(id);
        // The submitted plan is spent. Leaving it selected beside a
        // «تم التعيين» notice is an invitation to submit it again, and
        // the server's answer to that is 409 `same_plan`.
        setPlanKey('');
        setPlanReason('');
        return loadDetail(id);
      },
      // The CAUGHT error, classified — not the runner's one-size sentence
      // decorated with a guess. See {@link planAssignmentFailureMessage}.
      // It goes to the PLAN slot: a plan refusal has nothing to say about
      // a standing transition and must never surface in its dialog.
      setActionError: (id, message, cause) =>
        setPlanErrorFor(
          id,
          message === null ? null : planAssignmentFailureMessage(cause),
          organizationName,
        ),
    });
  };

  const rendered = detail?.status === 'ready';
  // ONE `attached` value for both slots, so the two can never disagree
  // about which organization is on screen.
  const attached = attachedIds([organizationId], rendered);
  const unattached = unattachedActionErrors(actionErrors, attached);
  const unattachedPlan = unattachedActionErrors(planErrors, attached);

  // Derived from the LIVE detail status rather than remembered: the
  // notice says "applied, refresh failed" exactly while the panel is in
  // its error state, and turns back into a plain "applied" the moment a
  // retry succeeds. One value, and it decides both the notice and what
  // the failed read is allowed to claim about the record.
  const planNotice = planCommitNotice({
    committedFor: planCommittedFor,
    organizationId,
    detailStatus: detail?.status ?? null,
  });
  const hasCommittedChange =
    planNotice !== null || (standingCommittedFor === organizationId && detail?.status === 'error');

  return (
    <div className="page op-console" dir="rtl" data-testid="platform-agency-detail-page">
      <p className="op-back">
        <Link className="btn btn-text btn-sm" to={backPath} data-testid="op-back-to-list">
          ← كل الوكالات
        </Link>
      </p>

      <UnattachedActionErrors
        failures={unattached}
        onDismiss={(id) => setActionErrors((prev) => withActionError(prev, id, null))}
      />
      <UnattachedActionErrors
        slug="plan"
        failures={unattachedPlan}
        onDismiss={(id) => setPlanErrors((prev) => withActionError(prev, id, null))}
      />

      {/* ABOVE the three panel states, because it has to be readable in
          all of them — most of all in the error state, which is the one
          the operator reaches when the refresh after a commit fails. */}
      <PlanCommitBanner notice={planNotice} />

      {detail === null || detail.status === 'loading' ? (
        <LoadingState title="جاري تحميل ملف الوكالة…" />
      ) : null}

      {detail?.status === 'error' ? (
        isApiError(detail.error) && detail.error.code === 'not_found' ? (
          <NotFoundState
            title="لا توجد وكالة بهذا المعرّف"
            description="ربما حُذف الرابط أو نُسخ خطأً. عد إلى القائمة واختر وكالة منها."
            homePath={backPath}
          />
        ) : (
          // The description is NOT a constant: after a committed plan
          // assignment «لم يتغيّر أي سجل» is false, and it is printed
          // beside a retry button that would re-submit the write.
          <ErrorState
            title="تعذّر تحميل ملف الوكالة"
            description={detailLoadErrorDescription(hasCommittedChange)}
            error={detail.error}
            onRetry={() => void loadDetail(organizationId)}
          />
        )
      ) : null}

      {data ? (
        <AgencyDetailView
          data={data}
          busy={busy}
          actionError={actionErrors[organizationId]?.message ?? null}
          planError={planErrors[organizationId]?.message ?? null}
          pending={pending}
          reason={reason}
          confirmName={confirmName}
          planKey={planKey}
          planReason={planReason}
          planBlocker={planBlocker}
          onRequestAction={(action) =>
            setPending({ action, organizationName: data.organization.name })
          }
          onCancelAction={() => {
            setPending(null);
            setConfirmName('');
          }}
          onReasonChange={setReason}
          onConfirmNameChange={setConfirmName}
          onConfirmAction={(action) => runAction(action, data.organization.name)}
          onPlanKeyChange={(next) => {
            setPlanKey(next);
            setPlanBlocker(null);
          }}
          onPlanReasonChange={(next) => {
            setPlanReason(next);
            setPlanBlocker(null);
          }}
          onAssignPlan={() => runPlanAssignment(data.organization.name)}
        />
      ) : null}
    </div>
  );
}

/**
 * The four plan-assignment failures an operator can act on differently.
 *
 * `same_plan` and `plan_not_assignable` are the two the SERVER names, on
 * a 409, in `error.detail` — the only wire code allowed to carry a safe
 * detail string at all (`_shared/errors.ts`, docs/10 §5.2). `conflict` is
 * a 409 whose detail is absent or is something this build has never heard
 * of. `unknown` is everything else: a network failure, a 401, a 404, a
 * 500 — the whole class of failures that says nothing about the plan the
 * operator picked.
 */
export type PlanAssignmentFailure = 'same_plan' | 'plan_not_assignable' | 'conflict' | 'unknown';

/**
 * Classify a caught plan-assignment failure. EXPORTED AND EXECUTED.
 *
 * It reads only `code` and the two `detail` values this product's own
 * endpoint emits. It never reads `message`, never echoes `detail` back
 * out, and treats an unrecognised detail as a bare conflict — so no
 * server string can reach the screen through here, whatever a future
 * endpoint decides to put in it.
 */
export function classifyPlanAssignmentFailure(error: unknown): PlanAssignmentFailure {
  // Anything that is not this product's own 409 says nothing about the
  // plan: a network failure, a 401, a 404 and a 500 are all `unknown`.
  if (!isApiError(error) || error.code !== 'conflict') return 'unknown';
  if (error.body.detail === 'same_plan') return 'same_plan';
  if (error.body.detail === 'plan_not_assignable') return 'plan_not_assignable';
  return 'conflict';
}

/**
 * What each classification says to the operator.
 *
 * `unknown` deliberately does NOT mention the plan at all. That was the
 * defect: every failure — a dropped connection, an expired session, a
 * 500 — was reported as "either it is the same plan or it is not
 * assignable", so the operator went looking at the select for a fault
 * that was never there and re-submitted the same correct plan until they
 * gave up. A message that is honestly vague beats one that is
 * confidently wrong.
 *
 * Every message also states what did NOT happen, because a failed
 * assignment leaves the plan exactly as it was and the operator's next
 * question is whether it did.
 */
export const PLAN_ASSIGNMENT_FAILURE_MESSAGES_AR: Readonly<Record<PlanAssignmentFailure, string>> =
  {
    same_plan: 'الوكالة على هذه الخطة بالفعل. اختر خطة مختلفة — لم يُسجَّل أي تغيير.',
    plan_not_assignable:
      'هذه الخطة غير متاحة للتعيين. حدّث الصفحة واختر من الخطط المتاحة — لم تتغيّر خطة الوكالة.',
    conflict: 'تعذّر تعيين الخطة بسبب تعارض في الحالة الحالية. حدّث الصفحة ثم أعد المحاولة.',
    unknown:
      'تعذّر تأكيد نتيجة تعيين الخطة. قد يكون الطلب وصل للخادم؛ أعد تحميل ملف الوكالة قبل أي محاولة جديدة.',
  };

/**
 * A plan-assignment failure, said in the operator's terms.
 *
 * `runCommercialAction` reports ONE generic message for every failure,
 * which is right for a standing transition: the state machine that
 * refused it is on the screen the operator is already reading. A plan
 * assignment has two refusals that are NOT visible anywhere on that
 * screen — the plan is the one the tenant is already on, or it is not in
 * the assignable catalogue — so the generic "check the current state and
 * try again" sends the operator to look at something that will not tell
 * them anything.
 *
 * This used to append BOTH of those possibilities to every failure it was
 * given, including the ones that were neither. It classifies now, from
 * the error the runner hands it, and falls back to an honestly generic
 * sentence rather than a confident wrong one.
 */
export function planAssignmentFailureMessage(error: unknown): string {
  return PLAN_ASSIGNMENT_FAILURE_MESSAGES_AR[classifyPlanAssignmentFailure(error)];
}

/**
 * The failures whose organization is not on screen — 306A §1.5.2 round 24,
 * reached here by NAVIGATION rather than by filtering.
 *
 * Start a transition, move to another agency while it is in flight, let it
 * reject: the failure belongs to a tenant this page is no longer showing,
 * and the panel that would have rendered it is gone. It renders at the top
 * instead, naming its agency, and it is dismissible because only that
 * agency's own next action would otherwise clear it.
 */
export function UnattachedActionErrors({
  failures,
  onDismiss,
  slug = 'action',
}: {
  readonly failures: ReadonlyArray<{ readonly organizationId: string } & CommercialActionError>;
  readonly onDismiss: (organizationId: string) => void;
  /**
   * Which SLOT these came from, so the standing list and the plan list
   * are addressable apart. One organization can hold an unread failure in
   * both — a refused transition, then a refused plan assignment — and two
   * banners carrying one id would otherwise be two elements with the same
   * test id and the same dismiss control.
   */
  readonly slug?: 'action' | 'plan';
}) {
  if (failures.length === 0) return null;
  return (
    <>
      {failures.map((failure) => (
        <p
          key={failure.organizationId}
          className="banner banner-error"
          role="alert"
          data-testid={`org-${slug}-error-unattached-${failure.organizationId}`}
        >
          {`${failure.organizationName}: ${failure.message}`}{' '}
          <button
            type="button"
            className="btn btn-text btn-sm"
            data-testid={`org-${slug}-error-dismiss-${failure.organizationId}`}
            onClick={() => onDismiss(failure.organizationId)}
          >
            إخفاء
          </button>
        </p>
      ))}
    </>
  );
}

/**
 * The outcome of a plan assignment that COMMITTED, as the operator has to
 * read it — Task 307A, the final correction pass.
 *
 * TWO STATES, AND THE DIFFERENCE IS NOT COSMETIC. `applied` is the write
 * plus a panel that has caught up with it. `applied_refresh_failed` is the
 * same write with a broken follow-up read, and it is the one the product
 * used to get wrong: the panel fell into its error state, which said «لم
 * يُفقد أي شيء ولم يتغيّر أي سجل» beside a retry button, so the operator
 * was told the assignment had not happened and invited to make it again.
 * The second attempt is the 409 `same_plan` — a second, equally
 * misleading, "failure" of an operation that succeeded the first time.
 */
export type PlanCommitNotice = {
  readonly status: 'applied' | 'applied_refresh_failed';
  readonly tone: 'arch' | 'warn';
  readonly message: string;
};

export const PLAN_COMMIT_MESSAGES_AR: Readonly<Record<PlanCommitNotice['status'], string>> = {
  applied: 'تم تغيير الخطة، وسُجِّل التغيير مع سببه في سجل الخطط.',
  applied_refresh_failed:
    'تم تغيير الخطة وسُجِّل في سجل الخطط، لكن تعذّر تحديث العرض بعده. ' +
    'لا تُعِد التعيين — أعد المحاولة لعرض الحالة المحدّثة.',
};

/**
 * What to say about a committed plan assignment, or `null` for nothing.
 *
 * DERIVED FROM THE LIVE PANEL STATUS rather than from what the refresh
 * happened to return once. A retry that succeeds turns the honest
 * "refresh failed" back into a plain "applied" without anything having to
 * remember to clear it, and the notice cannot outlive its own evidence.
 *
 * It is `null` for another agency's commit: this route re-renders in
 * place when the operator navigates, so a commit that lands after they
 * have moved on must not be reported over a tenant it says nothing about.
 *
 * Exported so the rule is EXECUTED rather than read off the JSX.
 */
export function planCommitNotice(input: {
  readonly committedFor: string | null;
  readonly organizationId: string;
  readonly detailStatus: DetailState['status'] | null;
}): PlanCommitNotice | null {
  // ONE comparison, and it covers "nothing committed" too: `null` is not
  // an organization id, so a page with no commit behind it matches
  // nothing. A separate null check would be a branch no input can take.
  if (input.committedFor !== input.organizationId) return null;
  // Only a FAILED read is a failed refresh. `loading` is a read still in
  // flight and `null` is one not yet issued; neither is evidence that the
  // panel will not catch up, and claiming otherwise would flash a warning
  // through every successful assignment.
  const status = input.detailStatus === 'error' ? 'applied_refresh_failed' : 'applied';
  return {
    status,
    tone: status === 'applied' ? 'arch' : 'warn',
    message: PLAN_COMMIT_MESSAGES_AR[status],
  };
}

/**
 * The notice, rendered — EXPORTED AND PROP-DRIVEN.
 *
 * `role="status"` and not `role="alert"`: this is the outcome of
 * something the operator just did, announced politely, and the page keeps
 * `role="alert"` for refusals. It sits ABOVE the three panel states
 * because the state it matters most in is the error one, where the panel
 * — and the plan controls with it — is not rendered at all.
 */
export function PlanCommitBanner({ notice }: { readonly notice: PlanCommitNotice | null }) {
  if (notice === null) return null;
  return (
    <p
      className={`banner op-banner tone-${notice.tone}`}
      role="status"
      data-testid={`op-plan-commit-${notice.status}`}
    >
      {notice.message}
    </p>
  );
}

/**
 * What a failed detail read may claim about the record.
 *
 * The generic sentence is true of a page that has only ever READ — which
 * is every failure of this load except one. After a committed plan
 * assignment it is false, and it is printed beside a retry control, which
 * is how an operator is talked into re-submitting a write the server has
 * already accepted.
 *
 * Exported and taking a boolean, because both plan assignments and
 * standing transitions can commit before their reconciliation read fails.
 */
export const DETAIL_LOAD_ERROR_DESCRIPTIONS_AR = {
  clean: 'لم يُفقد أي شيء ولم يتغيّر أي سجل. حاول مرة أخرى.',
  after_commit:
    'التغيير الذي نفّذته مسجَّل، والفشل هنا في تحديث العرض وحده. ' +
    'أعد المحاولة لعرض الحالة المحدّثة، ولا تُعِد تنفيذ التغيير.',
} as const;

export function detailLoadErrorDescription(hasCommittedChange: boolean): string {
  return hasCommittedChange
    ? DETAIL_LOAD_ERROR_DESCRIPTIONS_AR.after_commit
    : DETAIL_LOAD_ERROR_DESCRIPTIONS_AR.clean;
}

/**
 * The whole review screen — EXPORTED AND PROP-DRIVEN.
 *
 * Every rule this page has lives either in `operatorConsole` or in a
 * prop, so the markup is rendered by test rather than pinned as a source
 * string. That is the 301B lesson and 306A §2.5's: a pin proves an
 * expression is what it is, and can never prove it is right.
 */
export function AgencyDetailView({
  data,
  busy,
  actionError,
  planError,
  pending,
  reason,
  confirmName,
  planKey,
  planReason,
  planBlocker,
  onRequestAction,
  onCancelAction,
  onReasonChange,
  onConfirmNameChange,
  onConfirmAction,
  onPlanKeyChange,
  onPlanReasonChange,
  onAssignPlan,
}: {
  readonly data: OrganizationCommercialDetailData;
  readonly busy: boolean;
  /** The STANDING slot. Moves into the dialog while one is open. */
  readonly actionError: string | null;
  /** The PLAN slot. Renders beside the plan controls, and only there. */
  readonly planError: string | null;
  readonly pending: PendingDecision | null;
  readonly reason: string;
  readonly confirmName: string;
  readonly planKey: string;
  readonly planReason: string;
  readonly planBlocker: string | null;
  readonly onRequestAction: (action: CommercialAction) => void;
  readonly onCancelAction: () => void;
  readonly onReasonChange: (value: string) => void;
  readonly onConfirmNameChange: (value: string) => void;
  readonly onConfirmAction: (action: CommercialAction) => void;
  readonly onPlanKeyChange: (value: string) => void;
  readonly onPlanReasonChange: (value: string) => void;
  readonly onAssignPlan: () => void;
}) {
  const { organization, profile, profile_completion: completion, commercial, subscription } = data;
  const actions = actionsForState(commercial.commercial_state);
  const banner = standingBanner(commercial.commercial_state, completion);
  const failure = actionFailurePlacement(actionError, pending !== null);

  return (
    <div data-testid={`org-detail-panel-${organization.id}`}>
      <header className="page-head op-detail-head">
        <div>
          <h1>
            {organization.name} <StandingPill state={commercial.commercial_state} />
          </h1>
          <p className="sub">
            <bdi>
              {[
                displayValue(profile?.legal_name),
                displayValue(profile?.city),
                `التسجيل: ${formatOperatorTimestamp(organization.created_at)}`,
                `التفعيل: ${formatOperatorTimestamp(commercial.activated_at)}`,
              ].join(' · ')}
            </bdi>
          </p>
        </div>
      </header>

      <p className={`banner op-banner tone-${banner.tone}`} data-testid="op-standing-banner">
        <strong>{banner.title}</strong> {banner.body}
      </p>

      <div className="op-detail-grid">
        <div className="op-detail-main">
          <section className="card op-block">
            <div className="sec-title">
              <h2>مراجعة الملف النظامي</h2>
              {/* THE DENOMINATOR IS THE REQUIRED SET, and the sentence says
                  so. `completion.total` is the six fields Task 299 froze as
                  required — the same six
                  `app.set_organization_profile_completed_at()` scores — not
                  the eight displayed below. An unqualified "N of 8" over a
                  finished profile with no CR and no VAT invents a
                  requirement the product does not have, on the one screen
                  where "is this ready to activate" is the question. */}
              <span className="meta" data-testid={`org-profile-completion-${organization.id}`}>
                {completion.filled} من {completion.total} من الحقول المطلوبة ·{' '}
                {completionPercent(completion.filled, completion.total)}%
              </span>
            </div>

            {profile === null ? (
              <p className="tiny">لم تُسجّل الوكالة أي بيانات في ملفها بعد.</p>
            ) : (
              <>
                <ul className="op-checklist">
                  {AGENCY_PROFILE_FIELD_KEYS.map((key) => {
                    const value = profile[key];
                    const filled = typeof value === 'string' && value.trim().length > 0;
                    // MISSING and REQUIRED are different facts, and the
                    // checklist has to keep them apart: a blank optional
                    // field is not a reason to withhold activation, and a
                    // blank required one is. `completion.missing` is the
                    // server's own list of the second kind.
                    const blocking = (completion.missing as ReadonlyArray<string>).includes(key);
                    return (
                      <li
                        key={key}
                        className={`op-check${filled ? ' is-ok' : blocking ? ' is-missing' : ' is-optional'}`}
                        data-testid={`op-check-${key}`}
                      >
                        <span className="op-check-mark" aria-hidden="true">
                          {filled ? '✓' : '−'}
                        </span>
                        <span className="op-check-body">
                          <span className="tiny">{profileFieldLabel(key)}</span>
                          <span className="op-check-value">
                            <bdi>{displayValue(value)}</bdi>
                          </span>
                        </span>
                        <span className="op-sr">
                          {filled ? 'مستوفى' : blocking ? 'ناقص ومطلوب' : 'غير مستوفى واختياري'}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <p className="tiny">
                  السجل التجاري والرقم الضريبي اختياريان، ولا يمنع غيابهما اكتمال الملف ولا التفعيل.
                </p>
                <p className="tiny">
                  {/* The SERVER's own verdict, printed beside the derived
                      count rather than instead of it. Both now score the
                      same six fields, so a disagreement between them is a
                      real defect an operator can act on. */}
                  ختم الاكتمال من النظام:{' '}
                  <span dir="ltr">{formatOperatorTimestamp(profile.completed_at)}</span>
                </p>
              </>
            )}
          </section>

          <section className="card op-block">
            <div className="sec-title">
              <h2>سجل قرارات المنصة</h2>
            </div>
            {data.history.length === 0 ? (
              <p className="tiny">لا توجد قرارات مسجّلة على هذه الوكالة.</p>
            ) : (
              <ol className="op-timeline" data-testid={`org-history-${organization.id}`}>
                {data.history.map((event, index) => (
                  <li key={`${event.created_at}-${index}`} className="op-timeline-item">
                    <span className="op-timeline-title">
                      {LEDGER_ACTION_LABELS_AR[event.action] ?? event.action}
                    </span>
                    <span className="tiny">
                      <span dir="ltr">{formatOperatorTimestamp(event.created_at)}</span>
                      {' · '}
                      {COMMERCIAL_STATE_LABELS_AR[event.to_state as CommercialState] ??
                        event.to_state}
                      {event.from_payment_status !== event.to_payment_status
                        ? ` · الدفع: ${
                            PAYMENT_STATUS_LABELS_AR[event.to_payment_status] ??
                            event.to_payment_status
                          }`
                        : ''}
                    </span>
                    <span className="op-reason">{event.reason}</span>
                  </li>
                ))}
              </ol>
            )}
            {data.history_truncated ? (
              <p className="tiny">يُعرض أحدث ٥٠ قراراً فقط. السجل كامل ولا يُحذف منه شيء.</p>
            ) : null}
          </section>
        </div>

        <div className="op-detail-side">
          <section className="card op-block">
            <div className="sec-title">
              <h2>الحالة التجارية</h2>
            </div>
            <dl className="op-kv">
              <div className="op-kv-row">
                <dt className="tiny">اكتمال الملف</dt>
                <dd>
                  <CompletionMeter filled={completion.filled} total={completion.total} />
                </dd>
              </div>
              <KV label="الحالة" value={COMMERCIAL_STATE_LABELS_AR[commercial.commercial_state]} />
              <KV
                label="حالة الدفع اليدوي"
                value={
                  PAYMENT_STATUS_LABELS_AR[commercial.payment_status] ?? commercial.payment_status
                }
              />
              <KV label="تاريخ التفعيل" value={formatOperatorTimestamp(commercial.activated_at)} />
              <KV label="تاريخ الإيقاف" value={formatOperatorTimestamp(commercial.suspended_at)} />
              <KV label="تاريخ الإنهاء" value={formatOperatorTimestamp(commercial.terminated_at)} />
              <KV label="آخر تحديث" value={formatOperatorTimestamp(commercial.updated_at)} />
              <KV label="ملاحظات المنصة" value={displayValue(commercial.notes)} />
            </dl>
            <p className="tiny">
              {/* 295A D1 — payments are dormant and manual, and
                  payment_status gates nothing: `app.org_is_operational()`
                  does not read it, so an unrecorded bank transfer can never
                  become an outage. Said here because the operator is the
                  person who records it. */}
              حالة الدفع سجلٌّ محاسبي يدوي فقط، ولا تفتح أو تغلق أي صلاحية.
            </p>

            <div className="op-rail" data-testid="op-action-rail">
              {actions.map((action) => (
                <button
                  key={action}
                  type="button"
                  className={`btn ${isDestructiveAction(action) ? 'btn-danger' : 'btn-primary'}`}
                  disabled={busy}
                  data-testid={`org-action-${organization.id}-${action}`}
                  onClick={() => onRequestAction(action)}
                >
                  {ACTION_LABELS[action]}
                </button>
              ))}
              {actions.length === 0 ? (
                <p className="tiny" data-testid="op-no-actions">
                  لا توجد انتقالات متاحة. الحالة «منتهية» نهائية والسجل مؤرشف للقراءة فقط، ولا يُحذف
                  منه شيء.
                </p>
              ) : null}
              {failure.rail !== null ? (
                <p
                  className="banner banner-error"
                  role="alert"
                  data-testid={`org-action-error-${organization.id}`}
                >
                  {failure.rail}
                </p>
              ) : null}
            </div>
            <p className="tiny">
              كل انتقال يُسجَّل بالسبب والوقت في سجلّ لا يُعدَّل ولا يُحذف. لا يوجد حذف — الإنهاء
              يؤرشف السجل.
            </p>
          </section>

          <section className="card op-block">
            <div className="sec-title">
              <h2>اشتراك سوبر إيجنسي</h2>
            </div>
            {subscription === null ? (
              <p className="tiny" data-testid="op-no-subscription">
                لا يوجد اشتراك فعّال بعد. سيُنشأ اشتراك عند تعيين خطة.
              </p>
            ) : (
              <dl className="op-kv">
                <KV
                  label="الخطة"
                  value={planLabel(subscription.plan_key, subscription.plan_name_ar)}
                />
                <KV label="حالة الاشتراك" value={subscriptionStateLabel(subscription.state)} />
                <KV
                  label="بداية الاشتراك"
                  value={formatOperatorTimestamp(subscription.started_at)}
                />
                <KV
                  label="نهاية الدورة الحالية"
                  value={formatOperatorTimestamp(subscription.current_period_ends_at)}
                />
              </dl>
            )}

            <PlanAssignment
              organizationId={organization.id}
              currentPlanKey={subscription?.plan_key ?? null}
              assignable={data.assignable_plans}
              planKey={planKey}
              planReason={planReason}
              blocker={planBlocker}
              error={planError}
              busy={busy}
              onPlanKeyChange={onPlanKeyChange}
              onPlanReasonChange={onPlanReasonChange}
              onSubmit={onAssignPlan}
            />

            <p className="tiny">
              {/* The distinction the design brief requires to survive
                  implementation, said outright rather than implied by
                  layout. */}
              هذا اشتراك الوكالة في سوبر إيجنسي، وليس باقات الخدمات التي تبيعها الوكالة لعملائها.
              {/* The SAME clause `/app/billing` uses, verbatim. Task 300A's S2
                  scanner fails an electronic-payment token BY DEFAULT and
                  excuses only a clause already on `TRUTHFUL_NEGATIVE_CLAUSES`,
                  so restating this negative in fresh words would either fail
                  the scan or force the governance list to grow for a sentence
                  that already exists. Reusing it also means the agency and the
                  operator read the same words about one fact. */}
              لا يوجد دفع إلكتروني داخل المنتج. تغيير الخطة قرار يدوي من المنصة.
            </p>

            {data.plan_history.length > 0 ? (
              <ol className="op-timeline" data-testid={`org-plan-history-${organization.id}`}>
                {data.plan_history.map((event, index) => (
                  <li key={`${event.created_at}-${index}`} className="op-timeline-item">
                    <span className="op-timeline-title">
                      {event.from_plan_key
                        ? `${event.from_plan_key} ← ${event.to_plan_key}`
                        : event.to_plan_key}
                    </span>
                    <span className="tiny" dir="ltr">
                      {formatOperatorTimestamp(event.created_at)}
                    </span>
                    <span className="op-reason">{event.reason}</span>
                  </li>
                ))}
              </ol>
            ) : null}
            {data.plan_history_truncated ? (
              <p className="tiny">يُعرض أحدث ٥٠ تغييراً للخطة فقط.</p>
            ) : null}
          </section>

          <details className="card op-block op-technical">
            <summary className="tiny">تفاصيل تقنية</summary>
            {/* The ONLY surface allowed to show raw identifiers and enums,
                per the design. Everything above names them. */}
            <pre className="mono op-code" dir="ltr">
              {[
                `organization_id: ${organization.id}`,
                `slug: ${organization.slug}`,
                `commercial_state: ${commercial.commercial_state}`,
                `payment_status: ${commercial.payment_status}`,
                `plan_key: ${subscription?.plan_key ?? '-'}`,
                `subscription_state: ${subscription?.state ?? '-'}`,
              ].join('\n')}
            </pre>
          </details>
        </div>
      </div>

      {pending ? (
        <DecisionDialog
          action={pending.action}
          organizationName={pending.organizationName}
          reason={reason}
          confirmName={confirmName}
          busy={busy}
          failure={failure.dialog}
          onReasonChange={onReasonChange}
          onConfirmNameChange={onConfirmNameChange}
          onCancel={onCancelAction}
          onConfirm={() => onConfirmAction(pending.action)}
        />
      ) : null}
    </div>
  );
}

/**
 * Where this organization's failure has to be RENDERED — 307A.
 *
 * A refused transition leaves the dialog open, deliberately: closing it
 * would throw away the reason the operator typed, which is the one part
 * of the decision they cannot get back. But the failure banner lived in
 * the action rail, and the rail is BEHIND `aria-modal="true"` while the
 * dialog is up — so a screen-reader user was told nothing at all, and a
 * sighted one was told it behind a scrim. The operator saw the busy state
 * clear, the dialog stay open, and no explanation anywhere: the shape of
 * a transition that silently did nothing.
 *
 * So the failure moves INTO the dialog for as long as the dialog is open,
 * and back to the rail when it is not. Exactly ONE `role="alert"` either
 * way — two copies of one message is two announcements of one event.
 *
 * Exported so the placement is EXECUTED rather than read off the JSX.
 */
export function actionFailurePlacement(
  actionError: string | null,
  dialogOpen: boolean,
): { readonly dialog: string | null; readonly rail: string | null } {
  if (actionError === null) return { dialog: null, rail: null };
  return dialogOpen ? { dialog: actionError, rail: null } : { dialog: null, rail: actionError };
}

/**
 * The plan slot's refusal, rendered in ONE place and used by both of
 * {@link PlanAssignment}'s branches.
 *
 * A component and not a duplicated fragment: the empty-catalogue branch
 * and the form branch must report the same failure the same way, and two
 * copies of one banner is how one of them stops matching the other.
 */
function PlanFailure({ error }: { readonly error: string | null }) {
  if (error === null) return null;
  return (
    <p className="banner banner-error" role="alert" data-testid="op-plan-error">
      {error}
    </p>
  );
}

/**
 * The manual plan control — EXPORTED AND PROP-DRIVEN.
 *
 * The current plan is MARKED, not removed: same-plan is a SERVER refusal
 * (409 `same_plan`) and hiding the option would hide the one control that
 * demonstrates the rule is enforced. Marking it makes choosing it an
 * informed act rather than an accident.
 *
 * The options are the ACTIVE PUBLIC catalogue as the server reports it —
 * never a list written down in this file — so the select can never offer
 * a plan `set_organization_subscription_plan` would refuse as
 * unassignable.
 */
export function PlanAssignment({
  organizationId,
  currentPlanKey,
  assignable,
  planKey,
  planReason,
  blocker,
  error,
  busy,
  onPlanKeyChange,
  onPlanReasonChange,
  onSubmit,
}: {
  readonly organizationId: string;
  readonly currentPlanKey: string | null;
  readonly assignable: OrganizationCommercialDetailData['assignable_plans'];
  readonly planKey: string;
  readonly planReason: string;
  /** What THIS SCREEN refused. */
  readonly blocker: string | null;
  /**
   * What the SERVER refused, classified — and it renders HERE, beside the
   * select that produced it. It used to be written into the standing
   * slot, which `actionFailurePlacement` moves into the decision dialog:
   * «الوكالة على هذه الخطة بالفعل» could therefore be announced inside an
   * open «إنهاء» confirmation.
   */
  readonly error: string | null;
  readonly busy: boolean;
  readonly onPlanKeyChange: (value: string) => void;
  readonly onPlanReasonChange: (value: string) => void;
  readonly onSubmit: () => void;
}) {
  if (assignable.length === 0) {
    // The catalogue can empty out on a refresh AFTER a submission, so the
    // refusal is reported in this branch too rather than being lost with
    // the form that produced it.
    return (
      <>
        <p className="tiny" data-testid="op-plan-catalogue-empty">
          لا توجد خطط متاحة للتعيين حالياً.
        </p>
        <PlanFailure error={error} />
      </>
    );
  }
  return (
    <form
      className="op-plan-form"
      data-testid={`op-plan-form-${organizationId}`}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <label className="op-field">
        <span className="tiny">تغيير الخطة يدوياً</span>
        <select
          data-testid="op-plan-select"
          value={planKey}
          disabled={busy}
          onChange={(event) => onPlanKeyChange(event.target.value)}
        >
          <option value="">اختر خطة…</option>
          {assignable.map((plan) => (
            <option key={plan.key} value={plan.key}>
              {planLabel(plan.key, plan.name_ar)}
              {plan.key === currentPlanKey ? ' (الخطة الحالية)' : ''}
            </option>
          ))}
        </select>
      </label>
      <label className="op-field">
        <span className="tiny">السبب (مطلوب)</span>
        <textarea
          rows={2}
          maxLength={1000}
          data-testid="op-plan-reason"
          value={planReason}
          disabled={busy}
          onChange={(event) => onPlanReasonChange(event.target.value)}
        />
      </label>
      {blocker !== null ? (
        <p className="tiny op-blocker" role="alert" data-testid="op-plan-blocker">
          {blocker}
        </p>
      ) : null}
      <PlanFailure error={error} />
      <button
        type="submit"
        className="btn btn-ghost btn-sm"
        disabled={busy}
        data-testid="op-plan-submit"
      >
        تعيين الخطة
      </button>
    </form>
  );
}

/**
 * The decision dialog — EXPORTED AND PROP-DRIVEN.
 *
 * THREE GATES, IN THIS ORDER, and each exists for a stated reason:
 *
 *   1. a NON-BLANK reason, because 295A invariant A4 requires one on every
 *      transition and the server refuses a blank one too. This is the
 *      second line, not the line;
 *   2. the client-portal blast-radius WARNING before suspend and
 *      terminate, which 295A §5.4 requires the operator be shown BEFORE
 *      the action rather than after;
 *   3. for `terminate` only, the agency's name typed back. `terminated` is
 *      absorbing — no action moves out of it — so it is the one decision
 *      on this screen that cannot be undone from the product at all.
 *
 * The submit button stays ENABLED and the refusal is stated. A disabled
 * button with no explanation is a control that has stopped working for a
 * reason nobody told the operator.
 *
 * A SERVER FAILURE IS STATED IN HERE TOO, and that is not decoration. The
 * dialog stays open when a transition is refused — closing it would throw
 * away the typed reason — and everything outside it is inert behind
 * `aria-modal="true"`, so a banner rendered in the action rail is a
 * banner nobody is told about. `failure` is that report; it is separate
 * from `blocker`, because one is what THIS SCREEN refused and the other
 * is what the SERVER refused, and telling them apart is how the operator
 * knows whether to fix their input or to try again.
 */
export function DecisionDialog({
  action,
  organizationName,
  reason,
  confirmName,
  busy,
  failure,
  onReasonChange,
  onConfirmNameChange,
  onCancel,
  onConfirm,
}: {
  readonly action: CommercialAction;
  readonly organizationName: string;
  readonly reason: string;
  readonly confirmName: string;
  readonly busy: boolean;
  /** The last failure for this organization, or null. Optional so a
   *  caller with nothing to report need not pass it. */
  readonly failure?: string | null;
  readonly onReasonChange: (value: string) => void;
  readonly onConfirmNameChange: (value: string) => void;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}) {
  const blocker = decisionBlocker({ action, organizationName, reason, confirmName });
  const destructive = isDestructiveAction(action);
  return (
    <div className="op-modal-backdrop" data-testid="op-decision-dialog">
      <div
        className={`op-modal${destructive ? ' is-grave' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="op-decision-title"
      >
        <h2 id="op-decision-title" className="op-modal-title">
          {ACTION_LABELS[action]} · {organizationName}
        </h2>

        {destructive ? (
          <p className="banner banner-error" data-testid="op-destructive-warning">
            {DESTRUCTIVE_ACTION_WARNING}
          </p>
        ) : null}

        {action === 'terminate' ? (
          <p className="tiny" data-testid="op-terminate-note">
            «منتهية» حالة نهائية: لا يوجد إجراء يعيد الوكالة منها. لا يُحذف أي سجل، ويبقى الملف
            مؤرشفاً للقراءة.
          </p>
        ) : null}

        <label className="op-field">
          <span className="tiny">السبب (مطلوب، ويُسجَّل في سجلّ لا يُعدَّل ولا يُحذف)</span>
          <textarea
            rows={3}
            maxLength={1000}
            aria-label="السبب"
            data-testid="op-decision-reason"
            value={reason}
            onChange={(event) => onReasonChange(event.target.value)}
          />
        </label>

        {action === 'terminate' ? (
          <label className="op-field">
            <span className="tiny">اكتب اسم الوكالة للتأكيد: {organizationName}</span>
            <input
              type="text"
              data-testid="op-decision-confirm-name"
              value={confirmName}
              onChange={(event) => onConfirmNameChange(event.target.value)}
            />
          </label>
        ) : null}

        {blocker !== null ? (
          <p className="tiny op-blocker" role="alert" data-testid="op-decision-blocker">
            {DECISION_BLOCKER_MESSAGES_AR[blocker]}
          </p>
        ) : null}

        {/* The SERVER's refusal, inside the modal that is still open
            because of it. `role="alert"` so it is announced, and
            `aria-live` is not needed on top of it. */}
        {failure ? (
          <p className="banner banner-error" role="alert" data-testid="op-decision-failure">
            {failure}
          </p>
        ) : null}

        <div className="op-modal-actions">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            data-testid="op-decision-cancel"
            onClick={onCancel}
          >
            إلغاء
          </button>
          <button
            type="button"
            className={`btn btn-sm ${destructive ? 'btn-danger' : 'btn-accent'}`}
            disabled={busy}
            data-testid="op-decision-confirm"
            onClick={() => {
              if (blocker === null) onConfirm();
            }}
          >
            {ACTION_LABELS[action]}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Why this decision cannot be submitted yet, or `null`.
 *
 * Exported so the gate is EXECUTED by test rather than read off the JSX.
 * The name comparison is trimmed but case-sensitive and not normalised:
 * an agency's registered name is the string it registered, and a
 * "close enough" match is not a confirmation.
 */
export function decisionBlocker(input: {
  readonly action: CommercialAction;
  readonly organizationName: string;
  readonly reason: string;
  readonly confirmName: string;
}): 'no_reason' | 'name_mismatch' | null {
  if (input.reason.trim().length === 0) return 'no_reason';
  if (input.action === 'terminate' && input.confirmName.trim() !== input.organizationName.trim()) {
    return 'name_mismatch';
  }
  return null;
}

export const DECISION_BLOCKER_MESSAGES_AR: Readonly<
  Record<Exclude<ReturnType<typeof decisionBlocker>, null>, string>
> = {
  no_reason: 'السبب مطلوب لكل إجراء.',
  name_mismatch: 'اكتب اسم الوكالة كما هو للتأكيد.',
};

function KV({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="op-kv-row">
      <dt className="tiny">{label}</dt>
      {/* `bdi` isolates a value that may be Latin — a slug, an email, a CR
          number — inside an RTL definition list, where the bidi algorithm
          would otherwise reorder its internal punctuation. */}
      <dd>
        <bdi>{value}</bdi>
      </dd>
    </div>
  );
}
